import { notification } from "antd";
import cuid from "cuid";
import { get, isEmpty } from "lodash";
import PropTypes from "prop-types";
import { useContext, useState } from "react";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import { useMutation } from "shared/hooks/useApi";
import { StoreContext } from "shared/store";
import { CREATE_ATTACHMENT } from "utils/api/graphql/mutations/attachment";
import { ATTACHMENT_TYPES, CONTENT_TYPES } from "utils/constants";

const imageTypes = [CONTENT_TYPES.PNG, CONTENT_TYPES.JPEG, CONTENT_TYPES.JPG];

const buildFileList = (iconUrl) => {
  if (!iconUrl || !String(iconUrl).trim()) return [];
  return [
    {
      uid: "-1",
      name: "icon",
      status: "done",
      url: iconUrl,
      thumbUrl: iconUrl,
    },
  ];
};

const IconUploadField = ({
  label,
  buttonLabel,
  uploadingLabel,
  removeLabel,
  uploadErrorMessage,
  iconValue,
  setFieldValue,
}) => {
  const { user } = useContext(StoreContext);
  const [uploading, setUploading] = useState(false);
  const [addAttachment] = useMutation(CREATE_ATTACHMENT);

  const handleUpload = async (fileList) => {
    const file = fileList[0];
    if (!file?.base64) return;
    const userId = get(user, "id");
    if (!userId) {
      notification.error({ message: uploadErrorMessage, closeIcon: null });
      return;
    }
    setUploading(true);
    try {
      const { data } = await addAttachment({
        variables: {
          data: {
            id: cuid(),
            name: file.name,
            contentType: file.contentType,
            content: file.base64,
            type: ATTACHMENT_TYPES.ICON,
            user: { id: userId },
          },
        },
      });
      const fileUrl = get(data, "addAttachment.fileUrl");
      if (fileUrl) {
        setFieldValue("icon", fileUrl);
      } else {
        notification.error({ message: uploadErrorMessage, closeIcon: null });
      }
    } catch {
      notification.error({ message: uploadErrorMessage, closeIcon: null });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="email-template-icon-upload">
      <span className="input__label">{label}</span>
      <Input
        type="file"
        variant="uploader"
        listType="picture"
        showUploadList={false}
        maxCount={1}
        disabled={uploading}
        validTypes={imageTypes}
        onUpload={([file]) => handleUpload([file])}
        fileList={buildFileList(iconValue)}
      >
        <Button type="primary--link" disabled={uploading}>
          {uploading ? "…" : buttonLabel}
        </Button>
        <div className="email-template-icon-upload__preview">
          {!isEmpty(iconValue) && (
            <img
              className="email-template-icon-upload__img"
              alt=""
              src={iconValue}
            />
          )}
        </div>
      </Input>
      {!isEmpty(iconValue) && (
        <Button type="primary--link" onClick={() => setFieldValue("icon", "")}>
          {removeLabel}
        </Button>
      )}
    </div>
  );
};

IconUploadField.propTypes = {
  label: PropTypes.string.isRequired,
  buttonLabel: PropTypes.string.isRequired,
  uploadingLabel: PropTypes.string.isRequired,
  removeLabel: PropTypes.string.isRequired,
  uploadErrorMessage: PropTypes.string.isRequired,
  iconValue: PropTypes.string,
  setFieldValue: PropTypes.func.isRequired,
};

IconUploadField.defaultProps = {
  iconValue: "",
};

export default IconUploadField;
